import PropTypes from 'prop-types';
import qs from 'qs';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useSortBy } from 'react-instantsearch';

import Dropdown from '@atoms/Dropdown';
import { getAlgoliaIndexPrefix } from '@general/helpers/search.helpers';
import { getSortOption } from '@hooks/search/useSortBy';
import { sortOptionsByType } from '@react/data/sortOptions';
import { useSearchType } from '../../../context/searchTypeContext';

const queryParams = qs.parse(window.location.search.slice(1));
const algoliaPrefix = getAlgoliaIndexPrefix();

const buildAlgoliaOptions = searchTypeIndex => {
    const sortOptionsForType = sortOptionsByType[searchTypeIndex];
    if (!sortOptionsForType) return [];
    return sortOptionsForType.map(option => ({
        label: option.optionLabel,
        value: `${algoliaPrefix}_${searchTypeIndex}${option.indexSuffix}`,
    }));
};

export const SortByDropdown = ({ onSortByChange }) => {
    const [searchType] = useSearchType();
    const [sortBy, setSortBy] = useState();
    // Initialize with real options immediately so useSortBy never registers a
    // widget with items:[] — if it did, the subsequent items update would cause
    // useWidget to dispose+re-add the widget, and connectSortBy.dispose() resets
    // the index to the default, wiping out any non-default sort from initialUiState.
    const [sortOptionsForAlgolia, setSortOptionsForAlgolia] = useState(() =>
        buildAlgoliaOptions(searchType.index)
    );
    // Track the previous searchType.index so we only call refine when the search
    // type actually changes — not on initial mount. On mount, initialUiState already
    // applies the correct sort via getWidgetSearchParameters; calling refine here
    // would trigger helper.setIndex() → resetPage(), wiping out any page in the URL.
    const prevSearchTypeIndexRef = useRef(null);

    const { refine } = useSortBy({
        items: sortOptionsForAlgolia,
    });

    useEffect(() => {
        const sortOptionsForType = sortOptionsByType[searchType.index];

        if (!sortOptionsForType) {
            prevSearchTypeIndexRef.current = searchType.index;
            setSortOptionsForAlgolia([]);
            setSortBy();
            return;
        }

        setSortOptionsForAlgolia(buildAlgoliaOptions(searchType.index));

        const searchTypeChanged = prevSearchTypeIndexRef.current !== null &&
                                  prevSearchTypeIndexRef.current !== searchType.index;
        prevSearchTypeIndexRef.current = searchType.index;

        if (queryParams.sort) {
            const sortOptionFromQueryParam = getSortOption(searchType.index, queryParams.sort);
            setSortBy(sortOptionFromQueryParam);
            if (searchTypeChanged) {
                setTimeout(() => {
                    updateSort(sortOptionFromQueryParam.indexSuffix);
                }, 200);
            }
        } else {
            const defaultSort = sortOptionsForType.find(option => option.default);

            if (defaultSort) {
                setSortBy(sortOptionsForType.find(option => option.id === defaultSort.id));
                if (searchTypeChanged) {
                    setTimeout(() => {
                        updateSort(defaultSort.indexSuffix);
                    }, 100);
                }
            } else {
                const fallbackSort = sortOptionsForType[0];
                if (fallbackSort) {
                    setSortBy(fallbackSort);
                    if (searchTypeChanged) {
                        setTimeout(() => {
                            updateSort(fallbackSort.indexSuffix);
                        }, 100);
                    }
                }
            }
        }
    }, [searchType, updateSort]);

    const updateSort = useCallback(
        suffix => {
            refine(`${algoliaPrefix}_${searchType.index}${suffix}`);
        },
        [searchType, refine]
    );

    if (!sortOptionsForAlgolia) {
        return null;
    }

    return (
        <Dropdown
            options={sortOptionsByType[searchType.index] || []}
            selectedOption={sortBy}
            dropdownClasses="dropdown"
            dropdownItemClasses="dropdown-item my-3 pl-3"
            dropdownMenuClasses="dropdown-menu dropdown-menu-right m-0 mt-2 px-2 align-self-start"
            dropdownMenuLinkClasses="nav-link text-primary sort-list p-0"
            onOptionSelect={selectedOption => {
                setSortBy(selectedOption);
                onSortByChange(selectedOption);
                updateSort(selectedOption.indexSuffix);
            }}
        />
    );
};

SortByDropdown.propTypes = {
    onSortByChange: PropTypes.func.isRequired,
};
